import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from './ui/button';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

const VoiceInput = ({ onTranscript, disabled = false, className = '' }) => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  
  // Check if Web Speech API is supported
  const isSupported = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }, []);

  useEffect(() => {
    if (!isSupported) return;
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    // Initialize speech recognition
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'es-PE'; // Spanish (Peru)

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      
      if (event.error === 'not-allowed') {
        toast.error('Permiso de micrófono denegado. Por favor, habilite el acceso al micrófono.');
      } else if (event.error === 'no-speech') {
        toast.info('No se detectó voz. Intente de nuevo.');
      } else {
        toast.error('Error en reconocimiento de voz');
      }
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Send both final and interim results
      if (finalTranscript) {
        onTranscript(finalTranscript, true);
      } else if (interimTranscript) {
        onTranscript(interimTranscript, false);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onTranscript, isSupported]);

  const toggleListening = () => {
    if (!isSupported) {
      toast.error('Su navegador no soporta reconocimiento de voz');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      try {
        recognitionRef.current?.start();
        toast.info('Escuchando... Hable ahora', { duration: 2000 });
      } catch (error) {
        console.error('Error starting recognition:', error);
        toast.error('Error al iniciar el micrófono');
      }
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={toggleListening}
      disabled={disabled}
      className={`${className} ${
        isListening 
          ? 'bg-red-50 border-red-300 text-red-600 hover:bg-red-100 animate-pulse' 
          : 'border-[#c5b8a2] text-[#9d8272] hover:bg-[#9d8272]/10'
      }`}
      title={isListening ? 'Detener grabación' : 'Dictar descripción'}
    >
      {isListening ? (
        <>
          <MicOff size={16} className="mr-1" />
          <span className="text-xs hidden sm:inline">Detener</span>
        </>
      ) : (
        <>
          <Mic size={16} className="mr-1" />
          <span className="text-xs hidden sm:inline">Dictar</span>
        </>
      )}
    </Button>
  );
};

export default VoiceInput;
